import { useEffect, useRef } from "react";
import { usePlanStore } from "../../stores/planStore";
import { ChatMessage } from "../chat/ChatMessage";
import { ChatInput } from "../chat/ChatInput";
import { ChatDateTabs } from "../chat/ChatDateTabs";
import { PlanSuggestion } from "./PlanSuggestion";
import { MetricSuggestion } from "./MetricSuggestion";
import { TrashIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";

// Loading dots animation
function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-gray-100 rounded-lg px-4 py-2">
        <div className="flex gap-1">
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
        </div>
      </div>
    </div>
  );
}

export function CoachChat() {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const {
    activePlan,
    coachMessages,
    coachSessions,
    coachActiveDate,
    isCoachReadOnly,
    isCoachLoading,
    coachError,
    pendingSuggestion,
    readyForSuggestion,
    pendingMetricSuggestion,
    readyForMetricSuggestion,
    sendCoachMessage,
    clearCoachHistory,
    acceptSuggestion,
    declineSuggestion,
    acceptMetricSuggestion,
    declineMetricSuggestion,
    loadCoachHistory,
    loadCoachSessions,
    selectCoachDate,
    startCoachConversation,
  } = usePlanStore();

  // Load chat history and sessions on mount
  useEffect(() => {
    if (activePlan?.id) {
      loadCoachHistory(activePlan.id);
      loadCoachSessions(activePlan.id);
    }
  }, [activePlan?.id, loadCoachHistory, loadCoachSessions]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [coachMessages, isCoachLoading, pendingSuggestion, pendingMetricSuggestion]);

  const handleSend = (content: string) => {
    sendCoachMessage(content);
  };

  const handleClear = () => {
    clearCoachHistory();
  };

  const handleStartConversation = () => {
    startCoachConversation();
  };

  if (!activePlan) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="w-5 h-5 text-brand-600" />
          <h3 className="font-medium text-gray-900">Training Coach</h3>
        </div>
        {coachMessages.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            disabled={isCoachLoading}
            className="p-1 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
            title="Clear chat"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Date tabs */}
      <ChatDateTabs
        sessions={coachSessions}
        activeDate={coachActiveDate}
        onSelectDate={selectCoachDate}
      />

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {coachMessages.length === 0 && !isCoachLoading && !isCoachReadOnly && (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
            <ChatBubbleLeftRightIcon className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-sm">Your AI running coach is here to help</p>
            <p className="text-xs mt-1 text-gray-400 mb-4">
              Ask about your plan, suggest changes, or discuss your training
            </p>
            <button
              type="button"
              onClick={handleStartConversation}
              className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
            >
              Start Conversation
            </button>
          </div>
        )}

        {coachMessages.length === 0 && !isCoachLoading && isCoachReadOnly && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <ChatBubbleLeftRightIcon className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-sm text-gray-500">
              No messages on this day
            </p>
          </div>
        )}

        {coachMessages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {/* Plan suggestion - only show when AI is ready and not viewing past date */}
        {pendingSuggestion && readyForSuggestion && !isCoachReadOnly && (
          <PlanSuggestion
            suggestion={pendingSuggestion}
            onAccept={acceptSuggestion}
            onDecline={declineSuggestion}
            isLoading={isCoachLoading}
          />
        )}

        {/* Metric suggestion */}
        {pendingMetricSuggestion && readyForMetricSuggestion && !isCoachReadOnly && (
          <MetricSuggestion
            suggestion={pendingMetricSuggestion}
            onAccept={acceptMetricSuggestion}
            onDecline={declineMetricSuggestion}
            isLoading={isCoachLoading}
          />
        )}

        {isCoachLoading && <TypingIndicator />}

        {coachError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{coachError}</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="p-4 border-t border-gray-200 shrink-0">
        <ChatInput
          onSend={handleSend}
          disabled={isCoachLoading || isCoachReadOnly}
          placeholder={isCoachReadOnly ? "Past conversation (read-only)" : "Ask your coach..."}
        />
      </div>
    </div>
  );
}
